export const logOutFromLocal = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const refreshToken = JSON.parse(localStorage.getItem("session"))?.refreshToken;
      const token = JSON.parse(localStorage.getItem("session"))?.accessToken;
      const baseUrl = `${process.env.REACT_APP_DEV_API}`;
      
      const response = await fetch(`${baseUrl}/iam/sign-out`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken: refreshToken }),
      });

      if (response.ok) {
        localStorage.removeItem("user");
        localStorage.removeItem("session");
        resolve();
      } else {
        reject("Logout failed");
      }
    } catch (error) {
      console.error("Error:", error);
      reject(error);
    }
  });
};

export const saveUserToLocal = (user) => {
  localStorage.setItem("user", JSON.stringify(user));
};

// Fetches the signed-in user's current profile (with a fresh photo link)
// from the server. Returns null when not signed in. Throws on network errors.
export const fetchCurrentUser = async () => {
  const userId = JSON.parse(localStorage.getItem("user") || "null")?._id;
  const token = JSON.parse(localStorage.getItem("session") || "null")?.accessToken;
  if (!userId || !token) return null;
  const res = await fetch(`${process.env.REACT_APP_DEV_API}/user/${userId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Profile fetch failed (${res.status})`);
  const body = await res.json();
  return body?.data?.user || null;
};
export const getAccessToken = () => {
  const token = JSON.parse(localStorage.getItem("session"))?.accessToken;
  if (token) {
    return token;
  } else {
    logOutFromLocal();
    return null;
  }
};

// acceptedTermsVersion: passed from Register (box ticked) so the post-redirect sign-in can record it.
export const handleGoogleSignIn = (acceptedTermsVersion) => {
  if (typeof acceptedTermsVersion === 'string') sessionStorage.setItem('pendingTermsVersion', acceptedTermsVersion);
  else sessionStorage.removeItem('pendingTermsVersion');
  const nonce = Math.random().toString(36).substring(2) + Date.now().toString(36);
  localStorage.setItem('googleOAuthNonce', nonce);
  const oauth2Endpoint = 'https://accounts.google.com/o/oauth2/v2/auth';

  // Generate a random state value for security
  const state = Math.random().toString(36).substring(7);
  // Store state in localStorage to verify later
  localStorage.setItem('googleOAuthState', state);

  const params = {
    client_id: `${process.env.REACT_APP_GOOGLE_CLIENT_ID}`,
    redirect_uri: `${process.env.REACT_APP_GOOGLE_REDIRECT_URI}`,
    // id_token is what the server verifies (signature, audience, issuer, expiry).
    response_type: 'token id_token',
    nonce,
    scope: 'openid https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email',
    include_granted_scopes: 'true',
    state: state,
    prompt: 'select_account',
  };

  const form = document.createElement('form');
  form.setAttribute('method', 'GET');
  form.setAttribute('action', oauth2Endpoint);

  for (const p in params) {
    const input = document.createElement('input');
    input.setAttribute('type', 'hidden');
    input.setAttribute('name', p);
    input.setAttribute('value', params[p]);
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

export const handleGoogleCallback = async () => {
  try {
    // Get the hash fragment from the URL
    const fragment = window.location.hash.substring(1);
    const params = new URLSearchParams(fragment);
    
    // Get access token and state from URL
    const accessToken = params.get('access_token');
    const idToken = params.get('id_token');
    const receivedState = params.get('state');
    
    // Verify state to prevent CSRF attacks
    const savedState = localStorage.getItem('googleOAuthState');
    if (receivedState !== savedState) {
      throw new Error('Invalid state parameter');
    }
    
    // Clear the stored state
    localStorage.removeItem('googleOAuthState');
    if (!idToken) throw new Error('Google did not return an ID token');
    try {
      const claims = JSON.parse(atob(idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (claims.nonce !== localStorage.getItem('googleOAuthNonce')) throw new Error('nonce');
    } catch (e) { throw new Error('Invalid Google sign-in response'); }
    localStorage.removeItem('googleOAuthNonce');

    // Use the access token to get user info
    const response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get user info');
    }

    const data = await response.json();

    return {
      accessToken,
      idToken,
      email: data.email,
      name: data.name,
      picture: data.picture,
      verified_email: data.email_verified
    };

  } catch (error) {
    console.error('Error during Google OAuth callback:', error);
    throw error;
  }
};

// Facebook sign-in: one SDK load and init shared by Login and Register (same App ID).
// Errors carry a user-facing message; `cancelled` marks a user who closed/declined Facebook.
export class FacebookAuthError extends Error {
  constructor(message, { cancelled = false } = {}) { super(message); this.cancelled = cancelled; }
}

let fbReady = null;
export const loadFacebookSdk = () => {
  if (fbReady) return fbReady;
  fbReady = new Promise((resolve, reject) => {
    const init = () => {
      try {
        window.FB.init({ appId: `${process.env.REACT_APP_FACEBOOK_APP_ID}`, cookie: true, xfbml: false, version: 'v18.0' });
        resolve(window.FB);
      } catch (e) { reject(e); }
    };
    if (window.FB) return init();
    window.fbAsyncInit = init;
    if (!document.getElementById('facebook-jssdk')) {
      const js = document.createElement('script');
      js.id = 'facebook-jssdk';
      js.src = 'https://connect.facebook.net/en_US/sdk.js';
      js.async = true;
      js.onerror = () => { fbReady = null; reject(new Error('sdk')); };
      document.body.appendChild(js);
    }
  });
  fbReady.catch(() => { fbReady = null; });
  return fbReady;
};

// Must be called directly from the click so the Facebook window isn't blocked.
// Resolves with the Facebook access token that the server verifies.
export const handleFacebookLogin = () => new Promise((resolve, reject) => {
  if (!window.FB) {
    loadFacebookSdk().catch(() => {});
    return reject(new FacebookAuthError('Facebook is still loading or is blocked by this browser. Please wait a moment and try again.'));
  }
  try {
    window.FB.login((response) => {
      if (response?.status === 'connected' && response.authResponse?.accessToken) {
        return resolve({ accessToken: response.authResponse.accessToken, userID: response.authResponse.userID });
      }
      if (!response || !response.status || response.status === 'unknown' || response.status === 'not_authorized') {
        return reject(new FacebookAuthError('Facebook sign-in was cancelled.', { cancelled: true }));
      }
      reject(new FacebookAuthError('Facebook did not return a sign-in token. Please try again.'));
    }, { scope: 'public_profile,email', return_scopes: true, auth_type: 'rerequest' });
  } catch (e) {
    reject(new FacebookAuthError('Facebook sign-in could not start. Please try again.'));
  }
});

export const facebookErrorMessage = (error) =>
  error instanceof FacebookAuthError ? error.message : 'Facebook sign-in failed. Please try again.';
